import { ArrowLeftOutlined } from "@ant-design/icons";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import React, { useContext } from "react";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface SurveyFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const NauseaSurveyForm: React.FC<SurveyFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [form] = Form.useForm();
  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const userRole = sessionStorage.getItem("userRole");

  const { isLoading: isLoadingNauseaMutation, mutate: submitNauseaForm } =
    useMutation<string>(
      "add-bill",
      async () => {
        return await createApiClient().addNauseaFormData({
          patient_internal_id:
            userRole === UserRoles.PATIENT && userContext.userData
              ? userContext.userData?.internal_id
              : patientContext?.patientData?.id || "",
          nausea: "Yes",
          level: form.getFieldValue("Nausea Severity"),
          frequency: form.getFieldValue("Nausea Frequency"),
          submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
        });
      },
      {
        onSuccess: () => {
          message.success("Form Submitted Successfully");
          goToNextFormOrReturn();
        },
        onError: () => {
          message.error({
            content: (
              <span>
                There was a problem submitting this form, resubmit or{" "}
                <a onClick={() => goToNextFormOrReturn()}>
                  click here to continue to the next survey
                </a>
              </span>
            ),
            style: {
              bottom: "10px",
              position: "fixed",
              left: "30%"
            },
            duration: 5
          });
        }
      }
    );

  const onFinish = () => {
    submitNauseaForm();
  };

  return (
    <SurveyPage>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="nausea"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          <SurveyNoticeCard />

          <SurveyQuestionCard
            title="How often have you had nausea/vomiting in the last month?"
            required
          >
            <FormItemStyle
              name="Nausea Frequency"
              label=""
              rules={[{ required: true }]}
              initialValue=""
            >
              <Radio.Group style={{ width: "100%" }}>
                <AnswerList>
                  <AnswerOption>
                    <Radio value="Rarely (1 - 3 times)">
                      Rarely (1 - 3 times)
                    </Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="Occasionally (4 - 7 times)">
                      Occasionally (4 - 7 times)
                    </Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="Frequently (8 - 15 times)">
                      Frequently (8 - 15 times)
                    </Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="Nearly everyday">Nearly everyday</Radio>
                  </AnswerOption>
                </AnswerList>
              </Radio.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard
            title="How severe is your nausea/vomiting?"
            required
          >
            <FormItemStyle
              name="Nausea Severity"
              label=""
              rules={[{ required: true }]}
              initialValue=""
            >
              <Radio.Group style={{ width: "100%" }}>
                <AnswerList>
                  <AnswerOption>
                    <Radio value="Mild nausea only">Mild nausea only</Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="Moderate nausea, occasional vomiting">
                      Moderate nausea, occasional vomiting
                    </Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="Severe nausea / retching / vomiting">
                      Severe nausea / retching / vomiting
                    </Radio>
                  </AnswerOption>
                </AnswerList>
              </Radio.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyFooter>
            <SurveyProgressBar currentPage={1} totalPages={1} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={() => form.resetFields()} />
              </Form.Item>

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent="Submit"
                  htmlType="submit"
                  loading={isLoadingNauseaMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
